import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import {
  confirmPasswordReset,
  verifyPasswordResetCode,
  checkActionCode,
} from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertTriangleIcon,
  Loader2,
  CheckCircle,
  ArrowRight,
} from 'lucide-react'
import { z } from 'zod'
import { useEnvironmentUrls } from '@/hooks/use-environment'

const authActionSearchSchema = z.object({
  mode: z.string().optional(),
  oobCode: z.string().optional(),
  apiKey: z.string().optional(),
  continueUrl: z.string().optional(),
})

export const Route = createFileRoute('/auth-action')({
  component: AuthActionPage,
  validateSearch: authActionSearchSchema,
})

function AuthActionPage() {
  const search = useSearch({ from: '/auth-action' })
  const { mode, oobCode } = search

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordResetLoading, setPasswordResetLoading] = useState(false)
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false)

  // Get the appropriate login URL based on environment
  const { getLoginUrl } = useEnvironmentUrls()

  // Verify the action code and get user email
  useEffect(() => {
    const verifyActionCode = async () => {
      if (!oobCode || !mode) {
        setError('Invalid or missing action code.')
        setLoading(false)
        return
      }

      try {
        if (mode === 'resetPassword') {
          const userEmail = await verifyPasswordResetCode(auth, oobCode)
          setEmail(userEmail)
        } else if (mode === 'verifyEmail') {
          await checkActionCode(auth, oobCode)
          // Handle email verification if needed
        } else {
          setError('Unsupported action mode.')
        }
      } catch (error: unknown) {
        console.error('Action code verification failed:', error)
        const errorCode = (error as { code?: string })?.code
        switch (errorCode) {
          case 'auth/invalid-action-code':
            setError('This link is invalid or has already been used.')
            break
          case 'auth/expired-action-code':
            setError(
              'This link has expired. Please request a new password reset.',
            )
            break
          default:
            setError('An error occurred. Please try again.')
        }
      } finally {
        setLoading(false)
      }
    }

    verifyActionCode()
  }, [oobCode, mode])

  const handlePasswordReset = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      if (newPassword !== confirmPassword) {
        setError('Passwords do not match.')
        return
      }

      if (newPassword.length < 6) {
        setError('Password must be at least 6 characters.')
        return
      }

      setPasswordResetLoading(true)
      setError(null)

      try {
        await confirmPasswordReset(auth, oobCode!, newPassword)
        setPasswordResetSuccess(true)
      } catch (error: unknown) {
        console.error('Password reset failed:', error)
        const errorCode = (error as { code?: string })?.code
        switch (errorCode) {
          case 'auth/weak-password':
            setError('Password is too weak. Please choose a stronger password.')
            break
          case 'auth/invalid-action-code':
            setError('This link is invalid or has already been used.')
            break
          case 'auth/expired-action-code':
            setError(
              'This link has expired. Please request a new password reset.',
            )
            break
          default:
            setError('Failed to reset password. Please try again.')
        }
      } finally {
        setPasswordResetLoading(false)
      }
    },
    [newPassword, confirmPassword, oobCode],
  )

  const handleContinueToLogin = useCallback(() => {
    window.location.href = getLoginUrl()
  }, [getLoginUrl])

  if (loading) {
    return (
      <div className="flex h-screen w-screen flex-col overscroll-none md:flex-row">
        <main className="bg-background flex h-full w-full flex-1 flex-col items-center justify-center px-4">
          <div className="flex min-w-[350px] flex-col items-center gap-8">
            <img src="/assets/logo.svg" alt="Logo" className="h-15" />
            <Loader2 className="text-primary h-8 w-8 animate-spin" />
            <p className="text-copy-secondary text-sm">
              Verifying your request...
            </p>
          </div>
        </main>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen w-screen flex-col overscroll-none md:flex-row">
        <main className="bg-background flex h-full w-full flex-1 flex-col items-center justify-center px-4">
          <div className="flex min-w-[350px] flex-col items-start gap-8">
            <img src="/assets/logo.svg" alt="Logo" className="h-15" />
            <div className="flex w-full flex-col gap-1">
              <h1 className="text-2xl font-semibold">Something went wrong</h1>
              <span className="text-copy-secondary text-base font-normal">
                We encountered an issue processing your request.
              </span>
            </div>
            <div className="w-full space-y-4">
              <Alert variant="destructive">
                <AlertTriangleIcon className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <Button onClick={handleContinueToLogin} className="w-full">
                Continue to Login
              </Button>
            </div>
          </div>
        </main>
        <div className="bg-background hidden h-full w-full flex-1 md:block">
          <img
            src="/assets/login-img.png"
            alt="Login Background"
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    )
  }

  if (mode === 'resetPassword' && !passwordResetSuccess) {
    return (
      <div className="flex h-screen w-screen flex-col overscroll-none md:flex-row">
        <main className="bg-background flex h-full w-full flex-1 flex-col items-center justify-center px-4">
          <div className="flex min-w-[350px] flex-col items-start gap-8">
            <img src="/assets/logo.svg" alt="Logo" className="h-15" />
            <div className="flex w-full flex-col gap-1">
              <h1 className="text-2xl font-semibold">Reset your password</h1>
              <span className="text-copy-secondary text-base font-normal">
                for {email}
              </span>
            </div>
            <div className="w-full space-y-4">
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <Input
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={passwordResetLoading}
                  required
                  minLength={6}
                />
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={passwordResetLoading}
                  required
                  minLength={6}
                />
                <Button
                  type="submit"
                  disabled={
                    passwordResetLoading || !newPassword || !confirmPassword
                  }
                  className="w-full"
                >
                  {passwordResetLoading && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Save
                </Button>
              </form>

              {error && (
                <Alert variant="destructive">
                  <AlertTriangleIcon className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </main>
        <div className="bg-background hidden h-full w-full flex-1 md:block">
          <img
            src="/assets/login-img.png"
            alt="Login Background"
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    )
  }

  if (passwordResetSuccess) {
    return (
      <div className="flex h-screen w-screen flex-col overscroll-none md:flex-row">
        <main className="bg-background flex h-full w-full flex-1 flex-col items-center justify-center px-4">
          <div className="flex min-w-[350px] flex-col items-start gap-8">
            <img src="/assets/logo.svg" alt="Logo" className="h-15" />
            <div className="flex w-full flex-col gap-1">
              <h1 className="text-2xl font-semibold">Password changed</h1>
              <span className="text-copy-secondary text-base font-normal">
                You can now sign in with your new password
              </span>
            </div>
            <div className="w-full space-y-4">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-800">Success!</AlertTitle>
                <AlertDescription className="text-green-700">
                  Your password has been successfully updated. You can now use
                  your new password to sign in.
                </AlertDescription>
              </Alert>

              <Button onClick={handleContinueToLogin} className="w-full">
                <ArrowRight className="mr-2 h-4 w-4" />
                Continue to Login
              </Button>
            </div>
          </div>
        </main>
        <div className="bg-background hidden h-full w-full flex-1 md:block">
          <img
            src="/assets/login-img.png"
            alt="Login Background"
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    )
  }

  // Default fallback
  return (
    <div className="flex h-screen w-screen flex-col overscroll-none md:flex-row">
      <main className="bg-background flex h-full w-full flex-1 flex-col items-center justify-center px-4">
        <div className="flex min-w-[350px] flex-col items-start gap-8">
          <img src="/assets/logo.svg" alt="Logo" className="h-15" />
          <div className="flex w-full flex-col gap-1">
            <h1 className="text-2xl font-semibold">Action completed</h1>
            <span className="text-copy-secondary text-base font-normal">
              Your request has been processed successfully.
            </span>
          </div>
          <div className="w-full space-y-4">
            <Button onClick={handleContinueToLogin} className="w-full">
              Continue to Login
            </Button>
          </div>
        </div>
      </main>
      <div className="bg-background hidden h-full w-full flex-1 md:block">
        <img
          src="/assets/login-img.png"
          alt="Login Background"
          className="h-full w-full object-cover"
        />
      </div>
    </div>
  )
}
